import { z } from 'zod';

// Contact Form Schema
export const contactFormSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  phone: z.string().trim().optional().refine(
    (val) => !val || /^[\d\s\-\(\)\+]{7,20}$/.test(val),
    'Please enter a valid phone number'
  ),
  message: z.string().trim().min(10, 'Message must be at least 10 characters').max(1000, 'Message must be less than 1000 characters'),
  consentEmail: z.boolean().refine((val) => val === true, 'You must consent to receive email communications'),
  consentSms: z.boolean().optional(),
  acceptTerms: z.boolean().refine((val) => val === true, 'You must accept the Terms of Service and Privacy Policy'),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// Lead Form Schema
export const leadFormSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  phone: z.string().trim().min(7, 'Phone number is required').max(20, 'Phone number is too long').refine(
    (val) => /^[\d\s\-\(\)\+]{7,20}$/.test(val),
    'Please enter a valid phone number'
  ),
  service: z.string().min(1, 'Please select a service'),
  customerType: z.enum(['new', 'returning', 'current']).default('new'),
  message: z.string().trim().max(1000, 'Message must be less than 1000 characters').optional(),
  promoCode: z.string().trim().max(50, 'Promo code is too long').optional(),
  consentEmail: z.boolean().refine((val) => val === true, 'You must consent to receive email communications'),
  consentSms: z.boolean().optional(),
  acceptTerms: z.boolean().refine((val) => val === true, 'You must accept the Terms of Service and Privacy Policy'),
});

export type LeadFormData = z.infer<typeof leadFormSchema>;

// Newsletter Schema
export const newsletterSchema = z.object({
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
});

export type NewsletterFormData = z.infer<typeof newsletterSchema>;

// Quote Request Schema
export const quoteRequestSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  phone: z.string().trim().min(7, 'Phone number is required').max(20, 'Phone number is too long').refine(
    (val) => /^[\d\s\-\(\)\+]{7,20}$/.test(val),
    'Please enter a valid phone number'
  ),
  serviceType: z.string().min(1, 'Please select a service type'),
  projectDescription: z.string().trim().min(10, 'Please describe your project').max(2000, 'Description is too long'),
  budgetRange: z.string().optional(),
  timeline: z.string().optional(),
  propertyType: z.string().optional(),
  address: z.string().trim().max(255, 'Address is too long').optional(),
  preferredContactMethod: z.enum(['email', 'phone', 'text']).default('email'),
  preferredContactTime: z.string().optional(),
  howHeardAboutUs: z.string().optional(),
  consentEmail: z.boolean().refine((val) => val === true, 'You must consent to receive email communications'),
  consentSms: z.boolean().optional(),
  acceptTerms: z.boolean().refine((val) => val === true, 'You must accept the Terms of Service and Privacy Policy'),
});

export type QuoteRequestFormData = z.infer<typeof quoteRequestSchema>;
